import React, { useState, useEffect } from 'react';
import { GripVertical, Copy, Trash2, Edit } from 'lucide-react';
import { Soundworld } from '../../contexts/SoundworldsContext';
import { BuildProgressionModal } from './BuildProgressionModal';

const ROLE_ORDER = [
    'MELODY', 'LEAD',
    'CHORDS', 'OSTINATO',
    'TEXTURE',
    'BASS',
    'DRUM_KIT', 'DRUMS',
    'HIHAT', 'RIDE', 'CRASH',
    'TOMS', 'DRUMS_OTHER',
    'SNARE',
    'KICK',
    'PERCUSSION',
    'RISER', 'TRANSITION',
    'TAG', 'WATERMARK'
];

const normalizeKey = (value: string) => value.replace(/[^a-zA-Z]/g, '').toLowerCase();
const ORDER_NORMALIZED = ROLE_ORDER.map(normalizeKey);
const getRolePriority = (role: string) => {
    const n = normalizeKey(role);
    const idx = ORDER_NORMALIZED.indexOf(n);
    if (idx !== -1) return idx;
    for (let i = 0; i < ORDER_NORMALIZED.length; i++) {
        const key = ORDER_NORMALIZED[i];
        if (n.includes(key) || key.includes(n)) return i;
    }
    return Number.POSITIVE_INFINITY;
};

const ROLE_GROUPS: Record<string, string[]> = {
    LEAD_MELODY: ['LEAD', 'MELODY'],
    CHORDS_OSTINATO: ['CHORDS', 'OSTINATO'],
    TEXTURE: ['TEXTURE'],
    BASS: ['BASS'],
    DRUM_KIT_DRUMS: ['DRUM_KIT', 'DRUMS'],
    HIHAT_RIDE_CRASH: ['HIHAT', 'RIDE', 'CRASH'],
    TOMS_DRUMS_OTHER: ['TOMS', 'DRUMS_OTHER'],
    SNARE: ['SNARE'],
    KICK: ['KICK'],
    PERCUSSION: ['PERCUSSION'],
    RISER_TRANSITION: ['RISER', 'TRANSITION'],
    TAG_WATERMARK: ['TAG', 'WATERMARK'],
};

const ROLE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    LEAD_MELODY: { bg: '#E0FCED', border: '#CBF7E1', text: '#0B835A' },
    CHORDS_OSTINATO: { bg: '#DDF7FF', border: '#D0EFF7', text: '#0F738D' },
    TEXTURE: { bg: '#F5F0FF', border: '#E8DDFE', text: '#6B44BA' },
    BASS: { bg: '#E5EEFF', border: '#D6E5FF', text: '#245DC5' },
    DRUM_KIT_DRUMS: { bg: '#F5F0FF', border: '#E8DDFE', text: '#6B44BA' },
    HIHAT_RIDE_CRASH: { bg: '#F4FBCB', border: '#E9F797', text: '#566604' },
    TOMS_DRUMS_OTHER: { bg: '#FFF3CC', border: '#FFE59E', text: '#8D540A' },
    SNARE: { bg: '#FEEEE1', border: '#FFE59E', text: '#874D00' },
    KICK: { bg: '#FFEBEB', border: '#FFDCDB', text: '#C03636' },
    PERCUSSION: { bg: '#FEECF3', border: '#FDDDEA', text: '#B81C5D' },
    RISER_TRANSITION: { bg: '#FDEDFF', border: '#FAD6FF', text: '#A024AF' },
    TAG_WATERMARK: { bg: '#F8F9FA', border: '#EEEFF1', text: '#505154' },
};

const resolveRoleGroupKey = (role: string): string | null => {
    const upper = role.toUpperCase();
    for (const [groupKey, roles] of Object.entries(ROLE_GROUPS)) {
        if (roles.includes(upper)) return groupKey;
    }
    const rNorm = normalizeKey(upper);
    for (const [groupKey, roles] of Object.entries(ROLE_GROUPS)) {
        for (const rr of roles) {
            const rrNorm = normalizeKey(rr);
            if (rNorm.includes(rrNorm) || rrNorm.includes(rNorm)) return groupKey;
        }
    }
    return null;
};

const clamp = (val: number, min: number, max: number) => Math.min(max, Math.max(min, val));
const hexToRgb = (hex: string) => {
    const m = hex.replace('#', '');
    const bigint = parseInt(m, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
};
const rgbToHex = (r: number, g: number, b: number) =>
    '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
const shadeColor = (hex: string, percent: number) => {
    const { r, g, b } = hexToRgb(hex);
    const factor = 1 + percent / 100;
    const nr = clamp(Math.round(r * factor), 0, 255);
    const ng = clamp(Math.round(g * factor), 0, 255);
    const nb = clamp(Math.round(b * factor), 0, 255);
    return rgbToHex(nr, ng, nb);
};

const buildRoleCSS = () => {
    let css = '';
    for (const [groupKey, colors] of Object.entries(ROLE_COLORS)) {
        const baseBg = colors.bg;
        const baseBorder = colors.border;
        const baseText = colors.text;
        const hoverBg = shadeColor(baseBg, -8);
        const hoverBorder = shadeColor(baseBorder, -8);
        const activeBg = shadeColor(baseBg, -18);
        const activeBorder = shadeColor(baseBorder, -18);
        const selectedBg = shadeColor(baseBg, -22);
        const selectedBorder = shadeColor(baseBorder, -22);

        const darkBg = shadeColor(baseBg, -20);
        const darkBorder = shadeColor(baseBorder, -16);
        const darkHoverBg = shadeColor(baseBg, -26);
        const darkHoverBorder = shadeColor(baseBorder, -22);
        const darkActiveBg = shadeColor(baseBg, -34);
        const darkActiveBorder = shadeColor(baseBorder, -30);
        const darkSelectedBg = shadeColor(baseBg, -38);
        const darkSelectedBorder = shadeColor(baseBorder, -34);

        css += `
.role-btn-${groupKey} { background-color: ${baseBg}; border-color: ${baseBorder}; color: ${baseText}; }
.role-btn-${groupKey}:hover { background-color: ${hoverBg}; border-color: ${hoverBorder}; }
.role-btn-${groupKey}:active { background-color: ${activeBg}; border-color: ${activeBorder}; }
.role-btn-${groupKey}.is-selected { background-color: ${selectedBg}; border-color: ${selectedBorder}; box-shadow: 0 0 0 2px ${selectedBorder} inset; }
.dark .role-btn-${groupKey} { background-color: ${darkBg}; border-color: ${darkBorder}; color: ${baseText}; }
.dark .role-btn-${groupKey}:hover { background-color: ${darkHoverBg}; border-color: ${darkHoverBorder}; }
.dark .role-btn-${groupKey}:active { background-color: ${darkActiveBg}; border-color: ${darkActiveBorder}; }
.dark .role-btn-${groupKey}.is-selected { background-color: ${darkSelectedBg}; border-color: ${darkSelectedBorder}; box-shadow: 0 0 0 2px ${darkSelectedBorder} inset; }
`;
    }
    return css;
};

// Удалены дубликаты объявлений ниже

const getRoleStyles = (role: string) => {
    const groupKey = resolveRoleGroupKey(role);
    if (!groupKey) return undefined;
    return ROLE_COLORS[groupKey];
};

interface SongSectionProps {
    id: string;
    title: string;
    soundworld?: Soundworld;
    initialChords?: string;
    initialBars?: number;
    initialElements?: string[];
    onDuplicate: (id: string) => void;
    onDelete: (id: string) => void;
    onReorder: (fromId: string, toId: string) => void;
    onTitleChange: (id: string, newTitle: string) => void;
    onSectionDataChange: (id: string, data: {
        name: string;
        chords: string;
        elements: string[];
        bars: number;
    }) => void;
    isDragging?: boolean;
    dragOver?: boolean;
}

export const SongSection: React.FC<SongSectionProps> = ({
    id,
    title,
    soundworld,
    initialChords = '',
    initialBars = 8,
    initialElements = [],
    onDuplicate,
    onDelete,
    onReorder,
    onTitleChange,
    onSectionDataChange,
    isDragging = false,
    dragOver = false,
}) => {
    const [chords, setChords] = useState(initialChords);
    const [bars, setBars] = useState(initialBars);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editingTitle, setEditingTitle] = useState(title);
    const [selectedElements, setSelectedElements] = useState<string[]>(initialElements);
    const [isProgressionModalOpen, setIsProgressionModalOpen] = useState(false);

    useEffect(() => {
        if (initialChords !== chords) {
            setChords(initialChords);
        }
    }, [initialChords]);

    useEffect(() => {
        if (initialBars !== bars) {
            setBars(initialBars);
        }
    }, [initialBars]);

    useEffect(() => {
        if (JSON.stringify(initialElements) !== JSON.stringify(selectedElements)) {
            setSelectedElements(initialElements);
        }
    }, [initialElements]);

    const sendDataUpdate = (updatedChords?: string, updatedBars?: number) => {
        onSectionDataChange(id, {
            name: editingTitle,
            chords: updatedChords !== undefined ? updatedChords : chords,
            elements: selectedElements,
            bars: updatedBars !== undefined ? updatedBars : bars
        });
    };

    const handleTitleChange = () => {
        if (editingTitle.trim() !== '') {
            onTitleChange(id, editingTitle.trim());
            setTimeout(() => {
                sendDataUpdate();
            }, 0);
        }
        setIsEditingTitle(false);
    };

    const handleTitleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleTitleChange();
        } else if (e.key === 'Escape') {
            setEditingTitle(title);
            setIsEditingTitle(false);
        }
    };

    const handleChordsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newChords = e.target.value;
        setChords(newChords);
        sendDataUpdate(newChords);
    };

    const handleBarsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newBars = parseInt(e.target.value) || 8;
        setBars(newBars);
        sendDataUpdate(undefined, newBars);
    };

    const handleElementToggle = (role: string) => {
        setSelectedElements(prev => {
            const newElements = prev.includes(role)
                ? prev.filter(r => r !== role)
                : [...prev, role];

            setTimeout(() => {
                onSectionDataChange(id, {
                    name: editingTitle,
                    chords: chords,
                    elements: newElements,
                    bars: bars
                });
            }, 0);

            return newElements;
        });
    };

    return (
        <div
            className={ `bg-card rounded-lg p-4 mb-4 transition-all ${isDragging ? 'opacity-50' : ''
                } ${dragOver ? 'border-2 border-primary' : 'border border-border'}` }
            draggable
        >
            <style>
                { buildRoleCSS() }
            </style>
            {/* Header */ }
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="cursor-grab text-muted-foreground hover:text-card-foreground">
                        <GripVertical size={ 16 } />
                    </div>
                    { isEditingTitle ? (
                        <input
                            type="text"
                            value={ editingTitle }
                            onChange={ (e) => setEditingTitle(e.target.value) }
                            onBlur={ handleTitleChange }
                            onKeyDown={ handleTitleKeyPress }
                            className="font-semibold text-lg bg-transparent border border-border rounded px-2 py-1 focus:outline-none text-card-foreground"
                            autoFocus
                        />
                    ) : (
                        <h3
                            className="font-semibold text-lg cursor-pointer hover:bg-accent px-2 py-1 rounded text-card-foreground"
                            onClick={ () => setIsEditingTitle(true) }
                        >
                            { title }
                        </h3>
                    ) }
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-card-foreground">Bars:</label>
                        <input
                            type="number"
                            value={ bars }
                            onChange={ handleBarsChange }
                            min="1"
                            max="32"
                            className="w-16 px-2 py-1 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary text-center bg-background text-card-foreground"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-card-foreground">Chords:</label>
                        <div className="relative flex-1">
                            <input
                                type="text"
                                value={ chords }
                                onChange={ handleChordsChange }
                                className="w-full px-3 py-1 pr-8 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background text-card-foreground"
                                placeholder="Type your chord progression or use the editor"
                            />
                            <button
                                onClick={ () => setIsProgressionModalOpen(true) }
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                                title="Open progression editor"
                            >
                                <Edit size={ 14 } />
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={ () => onDuplicate(id) }
                        className="text-muted-foreground hover:text-card-foreground"
                        title="Duplicate section"
                    >
                        <Copy size={ 14 } />
                    </button>
                    <button
                        onClick={ () => onDelete(id) }
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete section"
                    >
                        <Trash2 size={ 14 } />
                    </button>
                </div>
            </div>

            {/* Elements Section */ }
            <div className="mb-4">
                <h4 className="font-medium text-sm mb-3 text-card-foreground">Elements</h4>
                <div className="grid grid-cols-6 gap-2">
                    { soundworld?.elements && typeof soundworld.elements === 'object' ? (
                        Object
                            .entries(soundworld.elements)
                            .sort(([roleA, elementA], [roleB, elementB]) => {
                                const effectiveRoleA = (elementA as any)?.role ?? roleA;
                                const effectiveRoleB = (elementB as any)?.role ?? roleB;
                                const a = getRolePriority(effectiveRoleA);
                                const b = getRolePriority(effectiveRoleB);
                                if (a !== b) return a - b;
                                return String(effectiveRoleA).localeCompare(String(effectiveRoleB)) || roleA.localeCompare(roleB);
                            })
                            .map(([role, element]: [string, any]) => {
                                const effectiveRole = (element as any)?.role ?? role;
                                const isSelected = selectedElements.includes(role);
                                const groupKey = resolveRoleGroupKey(effectiveRole);
                                return (
                                    <button
                                        key={ role }
                                        onClick={ () => handleElementToggle(role) }
                                        className={ `px-3 py-2 rounded text-sm transition-colors border ${isSelected ? 'font-semibold is-selected' : ''} ${groupKey ? `role-btn-${groupKey}` : ''}` }
                                        title={ effectiveRole }
                                    >
                                        { element.name_pretty || role }
                                    </button>
                                );
                            })
                    ) : (
                        <div className="col-span-6 text-center text-muted-foreground text-sm py-4">
                            No elements available
                        </div>
                    ) }
                </div>
            </div>

            <BuildProgressionModal
                isOpen={ isProgressionModalOpen }
                onClose={ () => setIsProgressionModalOpen(false) }
                initialChords={ chords }
                onSave={ (newChords) => {
                    setChords(newChords);
                    sendDataUpdate(newChords);
                    setIsProgressionModalOpen(false);
                } }
            />
        </div>
    );
}; 